import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Header } from '../components/Header';
import { SettlementForm } from '../components/SettlementForm';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { ProcessingTimeline, deriveSteps } from '../components/ProcessingTimeline';
import { ResultDashboard } from '../components/ResultDashboard';
import { BankConfirmationButton } from '../components/BankConfirmationButton';
import { ApprovalsQueue } from '../components/ApprovalsQueue';
import { useToast } from '../components/Toast';
import { forceBankResettleResync, getSettlementRails, newUuid } from '../lib/api';
import { isValid, type FormState } from '../lib/validation';
import type { ForceBankSettlementRequest, ForceBankSettlementResponse } from '../lib/types';
import { appConfig } from '../config';

const EMPTY: FormState = {
  koreId: '',
  bankRequestReference: '',
  bankName: '',
  settlementRail: '',
  accountIdentifier: '',
  operatorNotes: '',
  confirmed: false,
  sovereignOverride: false,
};

export function SettlementPage({ username }: { username: string }): JSX.Element {
  const toast = useToast();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [modalOpen, setModalOpen] = useState(false);
  const railsQuery = useQuery({
    queryKey: ['settlement-rails', form.koreId.trim()],
    queryFn: () => getSettlementRails(form.koreId.trim()),
    enabled: form.koreId.trim().length > 0,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const rails = railsQuery.data?.rails ?? [];
  const railsUnavailable = railsQuery.isError || (!railsQuery.isLoading && rails.length === 0);

  const mutation = useMutation<ForceBankSettlementResponse, Error & { body?: unknown }, ForceBankSettlementRequest>({
    mutationFn: forceBankResettleResync,
    onSuccess: (r) => {
      setModalOpen(false);
      toast.push(r.overallStatus === 'SUCCESS' ? 'success' : 'warn', `Settlement result: ${r.overallStatus}`);
    },
    onError: (e) => {
      setModalOpen(false);
      // The backend returns the real downstream body even on FAILED/REJECTED.
      const body = e.body as ForceBankSettlementResponse | undefined;
      if (body && typeof body === 'object' && 'overallStatus' in body) {
        toast.push('error', `Settlement ${body.overallStatus}. See details below.`);
      } else {
        toast.push('error', `Settlement request failed: ${e.message}`);
      }
    },
  });

  // Prefer the real response body even when the HTTP status was non-2xx (FAILED/REJECTED).
  const result: ForceBankSettlementResponse | null = useMemo(() => {
    if (mutation.data) return mutation.data;
    const body = mutation.error?.body as ForceBankSettlementResponse | undefined;
    if (body && typeof body === 'object' && 'overallStatus' in body) return body;
    return null;
  }, [mutation.data, mutation.error]);

  const steps = useMemo(() => deriveSteps(result, mutation.isPending), [result, mutation.isPending]);

  const submit = (): void => {
    const request: ForceBankSettlementRequest = {
      koreId: form.koreId.trim(),
      bankRequestReference: form.bankRequestReference.trim(),
      bankName: form.bankName.trim(),
      settlementRail: form.settlementRail,
      accountIdentifier: form.accountIdentifier.trim() || undefined,
      operatorNotes: form.operatorNotes.trim(),
      forceResettle: true,
      forceResync: true,
      syncToLinkedBankAccount: true,
      pacs028NudgeRequired: true,
      paymentConfirmationReconciliationRequired: true,
      requestedBy: appConfig.allowedUser,
      requestedAtUtc: new Date().toISOString(),
      idempotencyKey: newUuid(),
      correlationId: newUuid(),
      sovereignOverride: form.sovereignOverride,
    };
    mutation.mutate(request);
  };

  return (
    <div className="min-h-screen">
      <Header username={username} />
      <main className="mx-auto max-w-6xl px-4 py-6">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,420px)_1fr]">
          <div className="rounded-lg border border-slate-700 bg-kore-card p-5">
            <h2 className="mb-4 text-base font-semibold text-white">Force Bank Resettle &amp; Resync</h2>
            <SettlementForm
              form={form}
              onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
              onSubmit={() => { if (isValid(form)) setModalOpen(true); }}
              onClear={() => setForm(EMPTY)}
              submitting={mutation.isPending}
              rails={rails}
              railsLoading={railsQuery.isLoading}
              railsUnavailable={railsUnavailable}
            />
          </div>

          <div className="space-y-6">
            <div className="rounded-lg border border-slate-700 bg-kore-card p-5">
              <h2 className="mb-4 text-base font-semibold text-white">Processing Timeline</h2>
              <ProcessingTimeline steps={steps} />
            </div>

            {result && (
              <>
                <ResultDashboard result={result} />
                <BankConfirmationButton result={result} sovereignOverride={form.sovereignOverride} />
              </>
            )}

            <ApprovalsQueue defaultKoreId={form.koreId.trim()} />
          </div>
        </div>
      </main>

      <ConfirmationModal
        open={modalOpen}
        form={form}
        submitting={mutation.isPending}
        onCancel={() => setModalOpen(false)}
        onConfirm={submit}
      />
    </div>
  );
}
